import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { ASSubpageEnum } from '../../../enums';
import { OverlayType } from '../interfaces';
import i18next from 'i18next';
import SubpageWhite from './SubpageWhite';
import SspProviderContext from '../../../utils/SspProvider/context';
import Arrival from './Sections/Arrival';
import Delay from './Sections/Delay';
import Contact from './Sections/Contact';
import ChecklistPage from './Sections/Checklist';
import AdditionalServices from './Sections/AdditionalServices';
import OpeningHours from './Sections/OpeningHours';
import {
    dataNodesFromArray,
    experienceSliderStringArray,
    experienceStringArray,
    gastronomyStringArray,
    ritzCarltonStringArray,
    shopsStringArray,
    stringArrayToSliderTileArray,
    upgradesStringArray,
} from '../../../utils/AutostadtContextHelpers';
import { useConfig } from '../../../utils/AutostadtContextProvider';
import HappinessPlanner from './Sections/HappinessPlanner';
import OnlineCheckIn from './Sections/OnlineCheckIn';
import ProcedureOnSite from './Sections/ProcedureOnSite';
import Details from './Sections/Details';
import Discovery from './Sections/Discovery';
import ZoomableImage from '../../../pages/Map/MapContainer';

export default function ASSubpage(): JSX.Element | null {
    const store = useContext(SspProviderContext);
    const config = useConfig();
    const [show, setShow] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [lastScrollContainer, setLastScrollContainer] = useState<HTMLDivElement>();
    const scrollRef = useRef<HTMLDivElement>(null);
    const overlay = store.state.asSubpageStack[store.state.asSubpageStack.length - 1];

    const experienceArray = dataNodesFromArray(config, experienceStringArray);
    const upgradesArray = dataNodesFromArray(config, upgradesStringArray);
    const gastronomyArray = dataNodesFromArray(config, gastronomyStringArray);
    const shopsArray = dataNodesFromArray(config, shopsStringArray);

    const experienceSliderArray = stringArrayToSliderTileArray(config, experienceSliderStringArray);
    const ritzCarltonArray = dataNodesFromArray(config, ritzCarltonStringArray);

    useEffect(() => {
        const scrollContainer = scrollRef.current;

        if (!scrollContainer) return;

        if (lastScrollContainer !== scrollContainer) {
            setShow(true);
        }

        const handleScroll = () => {
            const currentScrollY = scrollContainer.scrollTop;

            if (currentScrollY > 80 && currentScrollY > lastScrollY) {
                setShow(false);
            } else {
                setShow(true);
            }
            setLastScrollY(currentScrollY);
        };

        setLastScrollContainer(scrollContainer);
        scrollContainer.addEventListener('scroll', handleScroll);

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [lastScrollY, store.state.asSubpageStack, lastScrollContainer, overlay]);

    useEffect(() => {
        const handlePopState = () => {
            if (store.state.asSubpageStack.length > 0) {
                store.dispatch({ type: 'CLOSE_AS_SUBPAGE' });
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [store, store.state.asSubpageStack]);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        // scroll to top if we are in subpages
        if (store.state.asSubpageStack.length >= 1) {
            scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [store.state.asSubpageStack]);



    function ASSubpageContent(overlay: OverlayType) {
        switch (overlay.asEnum) {
            case ASSubpageEnum.HappinessPlanner:
                return <HappinessPlanner />;
            case ASSubpageEnum.OnlineCheckIn:
                return <OnlineCheckIn />;
            case ASSubpageEnum.Delay:
                return <Delay />;
            case ASSubpageEnum.ProcedureOnSite:
                return <ProcedureOnSite />;
            case ASSubpageEnum.Arrival:
                return <Arrival />;
            case ASSubpageEnum.Contact:
                return <Contact />;
            case ASSubpageEnum.Checklist:
                return <ChecklistPage />;
            case ASSubpageEnum.OpeningHours:
                return <OpeningHours />;
            case ASSubpageEnum.Experience:
                return (
                    <Discovery
                        title={i18next.t('ExperienceTitle')}
                        imageSrc={'/AutostadtTab/entdecken.webp'}
                        imageCredits={''} //todo if we ever get them
                        subline={i18next.t('ExperienceSubline')}
                        array={experienceArray}
                        sliderArray={experienceSliderArray}
                    />
                );
            case ASSubpageEnum.Gastronomy:
                return (
                    <Discovery
                        title={i18next.t('GastronomyTitle')}
                        imageSrc={'/AutostadtTab/gastronomie.webp'}
                        imageCredits={''}//todo if we ever get them
                        subline={i18next.t('GastronomySubline')}
                        array={gastronomyArray}
                    />
                );
            case ASSubpageEnum.Shops:
                return (
                    <Discovery
                        title={i18next.t('ShopsTitle')}
                        imageSrc={'/AutostadtTab/shops.webp'}
                        imageCredits={''}//todo if we ever get them
                        subline={i18next.t('ShopsSubline')}
                        array={shopsArray}
                    />
                );
            case ASSubpageEnum.Upgrades:
                return (
                    <Discovery
                        title={i18next.t('UpgradesTitle')}
                        imageSrc={'/AutostadtTab/upgrades2.webp'}
                        imageCredits={''}//todo if we ever get them
                        subline={i18next.t('UpgradesSubline')}
                        array={upgradesArray}
                    />
                );
            case ASSubpageEnum.RitzCarlton:
                return (
                    <Discovery
                        title={dataNodesFromArray(config, ['The Ritz-Carlton, Wolfsburg'])[0].titel}
                        imageSrc={dataNodesFromArray(config, ['The Ritz-Carlton, Wolfsburg'])[0].bild_url}
                        imageCredits={''}//todo if we ever get them
                        subline={dataNodesFromArray(config, ['The Ritz-Carlton, Wolfsburg'])[0].content_full}
                        array={ritzCarltonArray}
                    />
                );
            case ASSubpageEnum.AdditionalServices:
                return <AdditionalServices />;
            case ASSubpageEnum.Map:
                return (
                    <SubpageWhite title={i18next.t('Map')} svg='as-swipe'>
                        <ZoomableImage src="/AutostadtTab/lageplan.webp" />
                    </SubpageWhite>
                );
            case ASSubpageEnum.Details:
                return <Details />;
            default:
                return <></>;
        }
    }

    return (
        <div className={`as-subpage-container`}>
            <div
                className={`as-subpage`}
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                <div className='as-subpage-wrapper'>
                    {Object.values(ASSubpageEnum).map(subpage => {
                        const isInStack = store.state.asSubpageStack.some(s => s.asEnum === subpage);
                        const isTop =
                            store.state.asSubpageStack[store.state.asSubpageStack.length - 1]?.asEnum === subpage;

                        return (
                            <div
                                className={`subpage ${isTop ? '--active' : isInStack ? '--stacked' : '--inactive'}`}
                                key={subpage}
                            >
                                <div className='scroll-container' ref={isTop ? scrollRef : null}>
                                    <div className={`page-header ${show ? '--visible' : '--hidden'}`}>
                                        <button
                                            className='back'
                                            onClick={() => {
                                                store.dispatch({ type: 'CLOSE_AS_SUBPAGE' });
                                            }}
                                        >
                                            <svg className='icon-24'>
                                                <use xlinkHref='#as-arrow-left' />
                                            </svg>
                                        </button>
                                        <svg className='header-title'>
                                            <use xlinkHref='#autostadt' />
                                        </svg>
                                    </div>

                                    {ASSubpageContent({ asEnum: subpage } as OverlayType)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
